import { auditFieldsForCreate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'

/**
 * Pusat Notifikasi (GEN-03, JAL-PG-40-002) — sisi server local mode.
 *
 * Tabel `notification` mengikuti ERD v010 (`id, user_id, role_id, type, title, message, link, channel, read_at,
 * dedupe_key, attempt_count, last_attempted_at, delivery_result, status` + kolom audit). Kolom di luar ERD:
 * `severity`, `ref_type`, `ref_id` (usulan D53 di ERD-EXTENSION-PROPOSAL). Pembuat = `user_created`, waktu
 * = `date_created`, jadi tidak ada kolom `created_by` / `created_at` tambahan.
 *
 * Pemakaian dari handler: `notify({ to: { roles: ['purchasing'] }, type: 'PR_PENDING_PURCHASING', title, body, link, ref })`.
 * `notify` TIDAK PERNAH melempar — kegagalan notifikasi tidak boleh membatalkan transaksi utama.
 *
 * Berkas ini tidak mengimpor `c07.shared` / `workflowEngine` supaya engine boleh memanggil aturan notifikasi
 * (`notification.rules.ts`) tanpa siklus impor.
 */

export const NOTIFICATION_COLLECTION = 'notification'

/**
 * Daftar jenis notifikasi. Salinan kontrak FE ada di
 * `service-library/.../general/constants/notification.constants.ts` (dicek uji `notification.handler.test.ts`).
 * `QC_DRAFT_SUBMITTED` / `QC_RETURNED` dipicu v7.50 Batch A (`inboundRmOperator.handler`); `OPNAME_TASK_ASSIGNED`
 * masih tanpa pemicu di local mode.
 */
export const NOTIFICATION_TYPES = [
  'PR_SUBMITTED',
  'PR_PENDING_PURCHASING',
  'PR_APPROVED',
  'PR_REJECTED',
  'ARRIVAL_PALLET_CLOSED',
  'TOBACCO_RECEIPT_PENDING',
  'TOBACCO_RECEIPT_STATUS',
  'QC_ASSIGNED',
  'QC_DRAFT_SUBMITTED',
  'QC_RETURNED',
  'PUTAWAY_ASSIGNED',
  'REQUEST_SUBMITTED',
  'REQUEST_STATUS',
  'PICKING_ASSIGNED',
  'RO_PENDING_APPROVAL',
  'RO_STATUS',
  'RO_PICKUP_ASSIGNED',
  'TRANSFER_PENDING_APPROVAL',
  'TRANSFER_STATUS',
  'OPNAME_STARTED',
  'OPNAME_TASK_ASSIGNED',
  'DISCREPANCY_ESCALATED',
  'DISCREPANCY_DECIDED',
  'CLAIM_SORT_ASSIGNED',
  'CLAIM_AWAITING_APPROVAL',
  'CLAIM_AUTO_APPROVED',
  'ALM_REQUEST_CREATED',
  'ALM_PENDING_APPROVAL',
  'ALM_AUTO_APPROVED',
  'GAB_AUTO_APPROVED',
  'DESTRUCTION_PENDING_APPROVAL',
  'DESTRUCTION_ASSIGNED',
  'MOVE_TASK_ASSIGNED',
  'CAPTURE_ASSIGNED',
  'CAPTURE_STATUS',
  'SYSTEM_INFO'
] as const

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

export const NOTIFICATION_SEVERITIES = ['info', 'success', 'warning', 'critical'] as const
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number]

export type NotifyLink = string | { route: string; query?: Record<string, string | number | null | undefined> }

export type NotifyInput = {
  /** Penerima: id user eksplisit, slug role (`mt_roles.code`), atau `broadcast` (semua user). */
  to: { userIds?: (string | null | undefined)[]; roles?: string[]; broadcast?: boolean }
  type: NotificationType
  title: string
  body: string
  /** Rute aplikasi (+ query) yang dibuka saat notifikasi diklik. */
  link?: NotifyLink
  /** Dokumen sumber, mis. `{ type: 'purchase_request', id }`. */
  ref?: { type: string; id: string }
  severity?: NotificationSeverity
  /** Bila diisi, notifikasi belum-dibaca dengan kunci sama untuk user yang sama tidak dibuat dua kali. */
  dedupeKey?: string
  /**
   * Sertakan user aktif sebagai penerima lewat `roles` / `broadcast`. Dipakai aksi sistem (sweep SLA): "user aktif" di sana
   * hanya orang yang kebetulan membuka halaman, bukan pelaku aksi, jadi tidak boleh dikecualikan.
   */
  includeActor?: boolean
}

export const WMS_ROUTE_BASE = '/solutions/wms'

/** `wmsRoute('procurement/daftar-pr', { id })` → `/solutions/wms/procurement/daftar-pr?id=…`. */
export function wmsRoute(path: string, query?: Record<string, string | number | null | undefined>): string {
  const base = `${WMS_ROUTE_BASE}/${path.replace(/^\/+/, '')}`
  const entries = Object.entries(query ?? {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== ''
  )
  if (!entries.length) return base
  return `${base}?${entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`).join('&')}`
}

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const linkOf = (link: NotifyLink | undefined): string | null => {
  if (!link) return null
  return typeof link === 'string' ? link : wmsRoute(link.route.replace(`${WMS_ROUTE_BASE}/`, ''), link.query)
}

/** Nama tampilan persona (first + last). */
export function userName(userId: unknown): string {
  const user = findRow('directus_users', idOf(userId))
  return user ? [user.first_name, user.last_name].filter(Boolean).join(' ') : ''
}

/** id user yang memegang salah satu role (slug `mt_roles.code`). */
export function userIdsOfRoles(roles: string[]): { userId: string; roleId: string }[] {
  const roleRows = readTable('mt_roles').filter(row => roles.includes(String(row.code)))
  return readTable('mt_privileges')
    .map(privilege => ({ userId: idOf(privilege.user), roleId: idOf(privilege.role) }))
    .filter(entry => roleRows.some(role => role.id === entry.roleId))
}

type Recipient = { userId: string; roleId: string | null }

/** Penerima unik. Pembuat aksi (user aktif) tidak diberi tahu lewat role/broadcast — hanya lewat `userIds` eksplisit. */
export function resolveRecipients(to: NotifyInput['to'], includeActor = false): Recipient[] {
  const actor = includeActor ? null : currentUserId()
  const picked = new Map<string, Recipient>()
  for (const userId of to.userIds ?? []) {
    const id = idOf(userId)
    if (id && findRow('directus_users', id) && !picked.has(id)) picked.set(id, { userId: id, roleId: null })
  }
  if (to.roles?.length) {
    for (const entry of userIdsOfRoles(to.roles)) {
      if (entry.userId !== actor && !picked.has(entry.userId)) {
        picked.set(entry.userId, { userId: entry.userId, roleId: entry.roleId })
      }
    }
  }
  if (to.broadcast) {
    for (const user of readTable('directus_users')) {
      if (user.id !== actor && !picked.has(user.id)) picked.set(user.id, { userId: user.id, roleId: null })
    }
  }
  return [...picked.values()]
}

/**
 * Buat notifikasi ke setiap penerima (satu baris per user, supaya status baca per persona). Mengembalikan baris yang
 * dibuat; `[]` bila tidak ada penerima atau terjadi galat — tidak pernah melempar.
 */
export function notify(input: NotifyInput): LocalRow[] {
  try {
    const now = new Date().toISOString()
    const link = linkOf(input.link)
    const created: LocalRow[] = []
    for (const recipient of resolveRecipients(input.to, input.includeActor)) {
      if (
        input.dedupeKey &&
        readTable(NOTIFICATION_COLLECTION).some(
          row => idOf(row.user_id) === recipient.userId && row.dedupe_key === input.dedupeKey && !row.read_at
        )
      )
        continue
      created.push(
        insertRow(NOTIFICATION_COLLECTION, {
          ...auditFieldsForCreate(),
          id: newId(),
          user_id: recipient.userId,
          role_id: recipient.roleId,
          type: input.type,
          title: input.title,
          message: input.body,
          link,
          channel: 'IN_APP',
          read_at: null,
          dedupe_key: input.dedupeKey ?? null,
          attempt_count: 1,
          last_attempted_at: now,
          delivery_result: 'DELIVERED',
          severity: input.severity ?? 'info',
          ref_type: input.ref?.type ?? null,
          ref_id: input.ref?.id ?? null
        })
      )
    }
    return created
  } catch (error) {
    console.warn('[local-db] notify gagal (diabaikan):', error)
    return []
  }
}

/* ------------------------------------------------------------------ baca / daftar */

const mine = () => readTable(NOTIFICATION_COLLECTION).filter(row => idOf(row.user_id) === currentUserId())

export type NotificationListParams = {
  unread?: boolean | string
  type?: string
  /** Awal & akhir periode (`YYYY-MM-DD`, hari lokal, inklusif). */
  from?: string
  to?: string
  search?: string
  page?: number | string
  limit?: number | string
  /** `all` = semua penerima (tab "Semua Persona"); pemeriksaan role ada di `notification.handler.ts`. */
  scope?: string
}

const asBool = (value: unknown) => value === true || value === 'true' || value === '1'
const startOfDay = (isoDate: string) => new Date(`${isoDate}T00:00:00`).getTime()
const endOfDay = (isoDate: string) => new Date(`${isoDate}T23:59:59.999`).getTime()

export const NOTIFICATION_MAX_LIMIT = 200

/** Baris notifikasi untuk FE: kolom ERD apa adanya + pembuat ter-resolve. */
export function notificationRow(row: LocalRow) {
  const sender = findRow('directus_users', idOf(row.user_created))
  const recipient = findRow('directus_users', idOf(row.user_id))
  const role = findRow('mt_roles', idOf(row.role_id))
  return {
    id: row.id,
    type: String(row.type ?? ''),
    title: String(row.title ?? ''),
    message: String(row.message ?? ''),
    link: (row.link as string | null) ?? null,
    severity: String(row.severity ?? 'info'),
    ref_type: (row.ref_type as string | null) ?? null,
    ref_id: (row.ref_id as string | null) ?? null,
    channel: String(row.channel ?? 'IN_APP'),
    read_at: (row.read_at as string | null) ?? null,
    date_created: String(row.date_created ?? ''),
    role_code: role ? String(role.code) : null,
    sender: sender
      ? { id: sender.id, first_name: String(sender.first_name ?? ''), last_name: String(sender.last_name ?? '') }
      : null,
    recipient: recipient
      ? {
          id: recipient.id,
          first_name: String(recipient.first_name ?? ''),
          last_name: String(recipient.last_name ?? '')
        }
      : null
  }
}

/**
 * `GET /api/v1/wms/notifications` — notifikasi milik user aktif, terbaru di atas; `scope=all` = semua penerima
 * (satu baris per penerima). `unread` selalu hitungan milik user aktif.
 */
export function listNotifications(params: NotificationListParams = {}) {
  const limit = Math.min(Math.max(Number(params.limit) || 10, 1), NOTIFICATION_MAX_LIMIT)
  const page = Math.max(Number(params.page) || 1, 1)
  const needle = String(params.search ?? '')
    .trim()
    .toLowerCase()
  const all = mine()
  const source = params.scope === 'all' ? readTable(NOTIFICATION_COLLECTION) : all
  const filtered = source
    .filter(row => {
      if (asBool(params.unread) && row.read_at) return false
      if (params.type && row.type !== params.type) return false
      const created = new Date(String(row.date_created)).getTime()
      if (params.from && created < startOfDay(params.from)) return false
      if (params.to && created > endOfDay(params.to)) return false
      if (needle && !`${row.title} ${row.message}`.toLowerCase().includes(needle)) return false
      return true
    })
    .sort((a, b) => String(b.date_created).localeCompare(String(a.date_created)))
  return {
    rows: filtered.slice((page - 1) * limit, page * limit).map(notificationRow),
    total: filtered.length,
    unread: all.filter(row => !row.read_at).length,
    page,
    limit
  }
}

/** `GET /api/v1/wms/notifications/unread-count`. */
export function unreadNotificationCount() {
  return { unread: mine().filter(row => !row.read_at).length }
}

/** `POST /api/v1/wms/notifications/read` — tandai dibaca (hanya milik user aktif; id asing diabaikan). */
export function markNotificationsRead(body: { ids?: string[] }) {
  const ids = new Set((body.ids ?? []).map(String))
  const now = new Date().toISOString()
  let updated = 0
  for (const row of mine()) {
    if (!ids.has(row.id) || row.read_at) continue
    updateRow(NOTIFICATION_COLLECTION, row.id, { read_at: now })
    updated += 1
  }
  return { updated, unread: unreadNotificationCount().unread }
}

/** `POST /api/v1/wms/notifications/read-all` — semua notifikasi belum dibaca milik user aktif. */
export function markAllNotificationsRead() {
  const now = new Date().toISOString()
  let updated = 0
  for (const row of mine()) {
    if (row.read_at) continue
    updateRow(NOTIFICATION_COLLECTION, row.id, { read_at: now })
    updated += 1
  }
  return { updated, unread: 0 }
}
